import { useMemo } from 'react';
import { Progress } from '@/components/ui/progress';

interface PasswordStrengthIndicatorProps {
  password: string;
}

const calculateStrength = (password: string): { score: number; label: string; color: string } => {
  let score = 0;
  
  if (password.length === 0) {
    return { score: 0, label: '', color: '' };
  }
  
  // Length checks
  if (password.length >= 6) score += 1;
  if (password.length >= 8) score += 1;
  if (password.length >= 12) score += 1;
  
  // Character variety checks
  if (/[a-z]/.test(password)) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;
  
  // Normalize to 0-100
  const normalizedScore = Math.min(100, (score / 7) * 100);
  
  if (normalizedScore < 30) {
    return { score: normalizedScore, label: 'Weak', color: 'bg-destructive' };
  } else if (normalizedScore < 60) {
    return { score: normalizedScore, label: 'Fair', color: 'bg-yellow-500' };
  } else if (normalizedScore < 80) {
    return { score: normalizedScore, label: 'Good', color: 'bg-blue-500' };
  } else {
    return { score: normalizedScore, label: 'Strong', color: 'bg-green-500' };
  }
};

const PasswordStrengthIndicator = ({ password }: PasswordStrengthIndicatorProps) => {
  const { score, label, color } = useMemo(() => calculateStrength(password), [password]);
  
  if (!password) return null;
  
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center">
        <span className="text-xs text-muted-foreground">Password strength</span>
        <span className={`text-xs font-medium ${
          label === 'Weak' ? 'text-destructive' :
          label === 'Fair' ? 'text-yellow-500' :
          label === 'Good' ? 'text-blue-500' :
          'text-green-500'
        }`}>
          {label}
        </span>
      </div>
      <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all duration-300 ${color}`}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;
